import * as path from 'path'
import { AgentProject } from '../agent-project/index.js'
import { formatCode } from '../generators/utils.js'
import { createFile } from '../utils/fs.js'
import { BuiltInWorkflows } from '@botpress/runtime/internal'

export type WorkflowTypeInfo = {
  name: string
  input: string
  output: string
  state: string
  requests: string
}

function isBuiltinWorkflow(name: string): boolean {
  return !!Object.values(BuiltInWorkflows).find((x) => x.name === name)
}

export async function generateWorkflowTypes(project: AgentProject): Promise<void> {
  const workflows: WorkflowTypeInfo[] = []

  // Load and process each workflow
  for (const workflowRef of project.workflows) {
    try {
      if (isBuiltinWorkflow(workflowRef.definition.name)) {
        // Skip built-in workflows - they're not importable from file paths
        continue
      }

      const workflowPath = path.join(project.path, workflowRef.path)
      // Bust module cache to ensure fresh workflow on type generation
      const workflowModule = await import(`${workflowPath}?t=${Date.now()}`)

      const workflowInstance = workflowModule[workflowRef.export] || workflowModule.default

      if (!workflowInstance) {
        continue
      }

      // Extract schemas and convert to TypeScript types
      const inputType = workflowInstance.inputSchema
        ? (workflowInstance.inputSchema as any).toTypescriptType?.() || 'any'
        : '{}'

      const outputType = workflowInstance.outputSchema
        ? (workflowInstance.outputSchema as any).toTypescriptType?.() || 'any'
        : '{}'

      const stateType = workflowInstance.stateSchema
        ? (workflowInstance.stateSchema as any).toTypescriptType?.() || 'any'
        : '{}'

      // Extract requests schemas
      let requestsType = '{}'
      const requestsSchemas = workflowInstance._requestsSchemas
      if (requestsSchemas && Object.keys(requestsSchemas).length > 0) {
        const requestTypes = Object.entries(requestsSchemas)
          .map(([key, schema]) => {
            const type = (schema as any).toTypescriptType?.() || 'any'
            return `${key}: ${type}`
          })
          .join('; ')
        requestsType = `{ ${requestTypes} }`
      }

      workflows.push({
        name: workflowRef.definition.name,
        input: inputType,
        output: outputType,
        state: stateType,
        requests: requestsType,
      })
    } catch (error) {
      console.error(`Failed to process workflow ${workflowRef.definition.name}:`, error)
    }
  }

  // Generate the type definitions file
  const typeDefinitions = workflows
    .map(
      (workflow) =>
        `    ${workflow.name}: {
      input: ${workflow.input};
      output: ${workflow.output};
      state: ${workflow.state};
      requests: ${workflow.requests};
    };`
    )
    .join('\n')

  const content = `// Generated workflow type definitions
// Do not edit this file directly - it will be overwritten

declare module "@botpress/runtime/_types/workflows" {
  export type WorkflowDefinitions = {
${typeDefinitions || '    // No workflows defined yet'}
  };
}`

  // Write the workflow types file
  const workflowTypesPath = path.join(project.path, '.adk', 'workflow-types.d.ts')
  await createFile(workflowTypesPath, await formatCode(content))
}
